import { useState, useEffect } from "react";
import axios from "axios";
import { Search, Loader2, Shield, Globe, User, Cpu, MapPin, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { FALLBACK_IMS_FACTS } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTITY_TYPE_ICONS = {
  person: User,
  ip_address: Cpu,
  location: MapPin,
  humint_source: Shield,
  stix_indicator: Activity,
  stix_malware: Activity,
  geospatial_feature: Globe,
};

export default function ImsFacts() {
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityName, setEntityName] = useState("");
  const [entityType, setEntityType] = useState("all");
  const [minThreat, setMinThreat] = useState("0");

  useEffect(() => {
    fetchFacts();
  }, []);

  const fetchFacts = async () => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (entityName) params.entity_name = entityName;
      if (entityType !== "all") params.entity_type = entityType;
      if (parseFloat(minThreat) > 0) params.min_threat = minThreat;

      const res = await axios.get(`${API}/ims/facts`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setFacts(res.data.length > 0 ? res.data : FALLBACK_IMS_FACTS);
    } catch (err) {
      setFacts(FALLBACK_IMS_FACTS);
      toast.error("Failed to fetch facts. Showing demo data.");
    }
    setLoading(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFacts();
  };

  const threatColor = (score) => {
    if (score >= 0.7) return "text-red-600 bg-red-50 border-red-200";
    if (score >= 0.4) return "text-amber-600 bg-amber-50 border-amber-200";
    if (score >= 0.2) return "text-cyan-600 bg-cyan-50 border-cyan-200";
    return "text-green-600 bg-green-50 border-green-200";
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
            <Search className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">
              FACTS BROWSER
            </h1>
            <p className="text-slate-600">Query normalised intelligence facts</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Entity Name</label>
          <Input
            value={entityName}
            onChange={e => setEntityName(e.target.value)}
            placeholder="Search..."
            className="w-48 rounded-none border-slate-300"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Entity Type</label>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="w-40 rounded-none bg-app border-slate-300">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-300 rounded-none">
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="person">Person</SelectItem>
              <SelectItem value="ip_address">IP Address</SelectItem>
              <SelectItem value="location">Location</SelectItem>
              <SelectItem value="humint_source">HUMINT Source</SelectItem>
              <SelectItem value="geospatial_feature">Geospatial</SelectItem>
              <SelectItem value="stix_indicator">STIX Indicator</SelectItem>
              <SelectItem value="stix_malware">STIX Malware</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Min Threat Score</label>
          <Input
            type="number"
            min="0"
            max="1"
            step="0.1"
            value={minThreat}
            onChange={e => setMinThreat(e.target.value)}
            className="w-24 rounded-none border-slate-300"
          />
        </div>
        <Button type="submit" className="rounded-none bg-emerald-600 hover:bg-emerald-500">
          <Search className="w-4 h-4 mr-1" /> Search
        </Button>
      </form>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : facts.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center">
          <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No facts found. Ingest some data first.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {facts.map((f) => {
            const Icon = ENTITY_TYPE_ICONS[f.entity_type] || Activity;
            return (
              <div key={f.id} className="bg-white border border-slate-200 p-4 hover:border-emerald-300 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                      <Icon className="w-4 h-4 text-slate-600" />
                    </div>
                    <div>
                      <span className="font-semibold text-slate-800">{f.entity_name}</span>
                      <span className="text-xs text-slate-400 ml-2">{f.entity_type}</span>
                    </div>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 border rounded ${threatColor(f.threat_score ?? 0)}`}>
                    {(f.threat_score ?? 0).toFixed(2)}
                  </span>
                </div>
                {f.summary && (
                  <p className="text-sm text-slate-600 ml-10">{f.summary}</p>
                )}
                <div className="flex items-center gap-3 mt-1 ml-10 text-xs text-slate-400">
                  <span>{f.timestamp?.slice(0, 19).replace("T", " ")}</span>
                  <span>conf: {f.confidence?.toFixed(2)}</span>
                  {f.location_lat && <span>{f.location_lat.toFixed(3)}, {f.location_lon?.toFixed(3)}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
